<template>
  <div class="smoke-runner">
    <p class="tool-intro">
      Runs scripted end-to-end checks against the <strong>live dev database</strong> as the
      <strong>real signed-in user</strong> — the same auth token and security-rules path production
      users hit. Destructive scenarios create tagged <code>SMOKE</code> documents and delete them
      when done (cleanup also runs if a step fails). Results stream below and can be exported as
      JSON. Reports also save automatically to dev Firebase, including progress, failures, cleanup
      and role restoration. Student records, email addresses and credentials are excluded.
    </p>

    <div v-if="!available" class="unavailable">
      Smoke runner is only available on the dev/test app (project
      <code>casemangervue</code>) in non-hardened builds.
    </div>

    <template v-else>
      <div class="role-context">
        <strong>Per-role coverage:</strong> live probes use the <em>JWT role</em> ({{
          jwtRole || 'none'
        }}), not the Debug Role Switcher. Sign in with each test-role account and re-run, or use
        <strong>Complete check</strong> on the allowlisted demo login (rotates in this session — no
        reload). Pick which rotator roles to include. District Admin is included on the dev demo
        login. The role contract matrix covers every role at once on the client side.
      </div>

      <div v-if="authBlocked" class="spoof-banner">
        {{ authBlockReason }}
        <button type="button" class="spoof-reset" @click="clearSpoofAndReload">
          Clear debug spoof and reload
        </button>
      </div>

      <div v-if="pendingProfile" class="spoof-banner">
        An interrupted smoke check has a saved starting role and school scope. Restore them before
        running another check.
        <button type="button" class="spoof-reset" :disabled="isRunning" @click="recoverProfile">
          Restore starting role and scope
        </button>
      </div>

      <div v-if="leftoverLine" class="leftover-line" :class="{ warn: leftoverWarn }">
        {{ leftoverLine }}
      </div>

      <div class="preset-row">
        <span class="preset-label">Preset</span>
        <button
          type="button"
          class="preset-btn"
          :class="{ active: !includeWrites }"
          :disabled="isRunning"
          @click="applyPreset(false)"
        >
          Read-only
        </button>
        <button
          type="button"
          class="preset-btn"
          :class="{ active: includeWrites }"
          :disabled="isRunning"
          @click="applyPreset(true)"
        >
          Include writes
        </button>
        <button type="button" class="preset-btn" :disabled="isRunning" @click="applyReferralPreset">
          Referral access
        </button>
        <button
          type="button"
          class="preset-btn"
          :disabled="isRunning"
          @click="applyWorkflowPreset('importWorkflow')"
        >
          Import workflow
        </button>
        <button
          type="button"
          class="preset-btn"
          :disabled="isRunning"
          @click="applyWorkflowPreset('districtAccess')"
        >
          District roles
        </button>
        <button
          type="button"
          class="preset-btn"
          :disabled="isRunning"
          @click="applyWorkflowPreset('saleReadiness')"
        >
          Sale readiness
        </button>
      </div>

      <p v-if="selected.includes('referralAccess')" class="role-context">
        <strong>Referral access:</strong> use <code>admin@casemanagevue.com</code>, select a school,
        confirm test writes, then choose <strong>Complete check</strong> to test the selected roles.
        Each role gets disposable pending, evaluation-owned and active-504 students. Setup briefly
        uses School Admin, then restores the real role before every access phase and when finished.
        Keep this tab open and leave the role unchanged while it runs. A single-role run proves only
        that role. Other staff accounts cannot create the fixtures and will report blocked.
      </p>

      <p
        v-if="
          selected.includes('importWorkflow') ||
          selected.includes('districtAccess') ||
          selected.includes('saleReadiness')
        "
        class="role-context"
      >
        Use <code>admin@casemanagevue.com</code>, confirm test writes, then
        <strong>Run smoke test</strong>. These checks prepare disposable campuses as District Admin
        and restore your starting role and <code>allSchools</code> flag afterward. They do not
        change your school assignments. Keep this tab open and do not switch accounts or roles while
        running. Each runs once if included in Complete check. The updated role-rotation function
        must be deployed.
      </p>
      <p v-if="selected.includes('districtAccess')" class="role-context">
        <strong>District profiles tested:</strong> District Admin, District Coordinator, District
        Case Manager, District Case Manager (All), and District Speech Case Manager. Case Manager
        (All) may read other active students; support-only assignments cannot edit, and unassigned
        Initials remain restricted.
      </p>

      <div class="scenario-list">
        <section v-for="g in scenarioGroups" :key="g.id" class="scenario-group">
          <label class="group-header">
            <input
              type="checkbox"
              :checked="groupState(g).all"
              :indeterminate="groupState(g).some"
              :disabled="isRunning"
              @change="toggleGroup(g)"
            />
            <span>
              <strong>{{ g.label }}</strong>
              <span v-if="g.write" class="destructive-tag">writes data</span>
              <span class="scenario-desc">{{ g.description }}</span>
            </span>
          </label>
          <label v-for="s in g.scenarios" :key="s.id" class="scenario-row">
            <input type="checkbox" v-model="selected" :value="s.id" :disabled="isRunning" />
            <span class="scenario-label">
              <strong>{{ s.label }}</strong>
              <span class="scenario-desc">{{ s.description }}</span>
            </span>
          </label>
        </section>
      </div>

      <label v-if="destructiveSelected" class="confirm-row">
        <input type="checkbox" v-model="confirmDestructive" :disabled="isRunning" />
        <span>
          I understand this creates and deletes real documents in the
          <strong>{{ projectId }}</strong> Firestore database (dev/test data only).
        </span>
      </label>

      <label v-if="destructiveSelected" class="keep-row">
        <input type="checkbox" v-model="keepArtifacts" :disabled="isRunning" />
        <span>
          Keep artifacts for debugging (skip Admin SDK cleanup after write roles). Default is off —
          leftovers are swept after each write role so CM / teacher / para creates do not pile up.
        </span>
      </label>

      <div v-if="canCompleteCheck" class="complete-note">
        <strong>Complete check</strong> rotates the roles you check below, in this session (no page
        reload), then restores {{ jwtRole || 'your starting role' }}. The District roles scenario
        also verifies all three district case-manager scopes.
        <label class="role-all">
          <input
            type="checkbox"
            :checked="allRolesSelected"
            :disabled="isRunning"
            @change="toggleAllRoles"
            ref="allRolesBox"
          />
          <span>All rotator roles ({{ completeRoleCount }})</span>
        </label>
        <div class="role-picks">
          <label v-for="role in rotatorRoles" :key="role.value" class="role-pick">
            <input
              type="checkbox"
              :value="role.value"
              v-model="selectedRoles"
              :disabled="isRunning"
            />
            <span>{{ role.label }}</span>
          </label>
        </div>
      </div>
      <div v-else-if="completeCheckBlockedReason" class="complete-note muted-note">
        {{ completeCheckBlockedReason }}
      </div>

      <details class="import-ui-checks" :open="selected.includes('importWorkflow')">
        <summary><strong>Import UI review — manual checks, saved to Firebase</strong></summary>
        <p>
          Use synthetic students on dev. The automated import check tests data and permissions;
          these boxes record what you personally verified in the wizard. Leave untested items
          unchecked. Unchecked items are saved as blocked, never passed.
        </p>
        <label v-for="item in importUiChecks" :key="item.id" class="keep-row">
          <input
            type="checkbox"
            v-model="manualChecks[item.id]"
            :disabled="isRunning || savingManual"
          />
          <span>{{ item.label }}</span>
        </label>
        <button
          class="btn btn-secondary"
          :disabled="isRunning || savingManual || authBlocked"
          @click="saveManualReview"
        >
          {{ savingManual ? 'Saving…' : 'Save UI check results to Firebase' }}
        </button>
        <button
          class="btn btn-secondary"
          :disabled="isRunning || savingManual"
          @click="resetManualReview"
        >
          Start a new UI review
        </button>
        <button
          v-if="manualReport"
          class="btn btn-secondary"
          :disabled="savingManual"
          @click="downloadJsonFile(manualReport, `${manualReport.runId}-ui-review.json`)"
        >
          Export UI review
        </button>
        <p v-if="manualSaveMessage" role="status">{{ manualSaveMessage }}</p>
      </details>

      <div class="run-row">
        <button @click="run" :disabled="runDisabled" class="btn btn-primary btn-with-icon">
          <Loader2 v-if="isRunning && runMode !== 'complete'" :size="14" class="spin" />
          <Bot v-else :size="14" />
          <span>{{ isRunning && runMode !== 'complete' ? 'Running…' : 'Run smoke test' }}</span>
        </button>
        <button
          v-if="canCompleteCheck"
          @click="runComplete"
          :disabled="runDisabled || selectedRoles.length === 0"
          class="btn btn-secondary btn-with-icon"
        >
          <Loader2 v-if="isRunning && runMode === 'complete'" :size="14" class="spin" />
          <Bot v-else :size="14" />
          <span>{{
            isRunning && runMode === 'complete'
              ? sweepProgressLine
              : `Complete check (${selectedRoles.length} role${selectedRoles.length === 1 ? '' : 's'})`
          }}</span>
        </button>
        <button
          v-if="report"
          @click="exportReport"
          :disabled="isRunning"
          class="btn btn-secondary btn-with-icon"
        >
          <Download :size="14" /> Export JSON report
        </button>
        <button
          @click="runCleanup"
          :disabled="isRunning || isCleaning"
          class="btn btn-secondary btn-with-icon"
          title="Delete any leftover SMOKE-tagged docs and files (e.g. from runs by roles that lack delete permission)."
        >
          <Loader2 v-if="isCleaning" :size="14" class="spin" />
          <Trash2 v-else :size="14" />
          Clean up leftovers
        </button>
      </div>

      <div v-if="cloudStatus" class="role-context" aria-live="polite">
        <strong>Firebase log:</strong>
        <template v-if="cloudStatus.status === 'saving'">saving…</template>
        <template v-else-if="cloudStatus.status === 'error'">
          Save failed: {{ cloudStatus.error }}. Export JSON as a fallback.
          <button v-if="!isRunning" class="preset-btn" @click="retryCloudSave">Retry save</button>
        </template>
        <template v-else>
          {{
            cloudStatus.state === 'running'
              ? 'Progress saved'
              : cloudStatus.state === 'aborted'
                ? 'Interrupted report saved'
                : 'Report saved'
          }}. Share this run ID for review; no JSON attachment needed.
        </template>
        <code>{{ cloudStatus.runId }}</code>
      </div>

      <div v-if="cleanupSummary" class="cleanup-summary">
        Cleanup: {{ cleanupSummary.deleted }} doc(s) and {{ cleanupSummary.storageDeleted }} file(s)
        removed.
        <span v-if="cleanupSummary.errors.length" class="step-error">
          {{ cleanupSummary.errors.length }} error(s): {{ cleanupSummary.errors[0] }}
        </span>
      </div>

      <div v-if="runError" class="run-error">{{ runError }}</div>

      <div v-if="hasResults" class="results">
        <div
          v-if="report"
          class="summary-line"
          :class="report.summary.fail ? 'has-fail' : 'all-pass'"
        >
          <CheckCircle v-if="!report.summary.fail" :size="16" />
          <XCircle v-else :size="16" />
          <template v-if="report.mode === 'complete'">
            {{ report.summary.rolesPass || 0 }} / {{ report.roles?.length || 0 }} roles passed
            <span class="muted">
              — {{ report.summary.pass }} steps passed, {{ report.summary.fail }} failed
              <template v-if="report.summary.skip">, {{ report.summary.skip }} skipped</template>
              <template v-if="report.summary.blocked">
                , {{ report.summary.blocked }} blocked
              </template>
              — {{ report.runId }}
            </span>
          </template>
          <template v-else>
            {{ report.summary.pass }} passed, {{ report.summary.fail }} failed
            <span v-if="report.summary.skip" class="muted">
              , {{ report.summary.skip }} skipped
            </span>
            <span v-if="report.summary.blocked" class="muted">
              , {{ report.summary.blocked }} blocked
            </span>
            <span class="muted">
              — run {{ report.runId }} as {{ report.user.email }} (JWT
              {{ report.user.role || 'no role' }})
            </span>
          </template>
        </div>

        <p v-if="report?.restoreError" class="run-error">
          Could not restore starting role {{ report.user.startingRole }}:
          {{ report.restoreError }}
        </p>

        <div v-if="report?.caveat" class="caveat">{{ report.caveat }}</div>

        <template v-if="reportedRoleGroups">
          <div v-for="group in reportedRoleGroups" :key="group.role" class="role-group">
            <div class="role-group-header">
              <component
                :is="
                  group.status === 'fail'
                    ? XCircle
                    : group.status === 'blocked'
                      ? AlertTriangle
                      : CheckCircle
                "
                :size="15"
                :class="
                  group.status === 'fail' ? 'fail' : group.status === 'blocked' ? 'blocked' : 'pass'
                "
              />
              <strong>{{ group.label }}</strong>
              <span class="muted">
                {{ group.summary.pass }} pass
                <template v-if="group.summary.fail"> · {{ group.summary.fail }} fail</template>
                <template v-if="group.summary.skip"> · {{ group.summary.skip }} skip</template>
                <template v-if="group.summary.blocked">
                  · {{ group.summary.blocked }} blocked
                </template>
              </span>
            </div>
            <p v-if="group.rotateError" class="step-error">{{ group.rotateError }}</p>
            <div v-for="s in group.scenarios" :key="s.id" class="scenario-result">
              <div class="scenario-result-header">
                <component
                  :is="s.failed ? XCircle : s.blocked ? AlertTriangle : CheckCircle"
                  :size="15"
                  :class="s.failed ? 'fail' : s.blocked ? 'blocked' : 'pass'"
                />
                <strong>{{ s.label }}</strong>
              </div>
              <div v-for="(r, i) in s.steps" :key="i" class="step-row" :class="r.status">
                <span class="step-icon">
                  <Loader2 v-if="r.status === 'running'" :size="13" class="spin" />
                  <XCircle v-else-if="r.status === 'fail'" :size="13" />
                  <AlertTriangle v-else-if="r.status === 'blocked'" :size="13" />
                  <MinusCircle v-else-if="r.status === 'skip'" :size="13" />
                  <CheckCircle v-else :size="13" />
                </span>
                <span class="step-name">{{ r.step }}</span>
                <span v-if="r.detail" class="step-detail">{{ r.detail }}</span>
                <span v-if="r.error" class="step-error">{{ r.error }}</span>
                <span v-if="r.durationMs != null" class="step-ms">{{ r.durationMs }}ms</span>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div v-for="g in reportedGroups" :key="g.id" class="role-group">
            <div class="role-group-header">
              <strong>{{ g.label }}</strong>
              <span v-if="g.write" class="destructive-tag">writes data</span>
            </div>
            <div v-for="s in g.scenarios" :key="s.id" class="scenario-result">
              <div class="scenario-result-header">
                <component
                  :is="s.failed ? XCircle : s.blocked ? AlertTriangle : CheckCircle"
                  :size="15"
                  :class="s.failed ? 'fail' : s.blocked ? 'blocked' : 'pass'"
                />
                <strong>{{ s.label }}</strong>
              </div>
              <div v-for="(r, i) in s.steps" :key="i" class="step-row" :class="r.status">
                <span class="step-icon">
                  <Loader2 v-if="r.status === 'running'" :size="13" class="spin" />
                  <XCircle v-else-if="r.status === 'fail'" :size="13" />
                  <AlertTriangle v-else-if="r.status === 'blocked'" :size="13" />
                  <MinusCircle v-else-if="r.status === 'skip'" :size="13" />
                  <CheckCircle v-else :size="13" />
                </span>
                <span class="step-name">{{ r.step }}</span>
                <span v-if="r.detail" class="step-detail">{{ r.detail }}</span>
                <span v-if="r.error" class="step-error">{{ r.error }}</span>
                <span v-if="r.durationMs != null" class="step-ms">{{ r.durationMs }}ms</span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, reactive, watch, onMounted } from 'vue'
import {
  Bot,
  Download,
  Loader2,
  CheckCircle,
  XCircle,
  Trash2,
  AlertTriangle,
  MinusCircle
} from 'lucide-vue-next'
import { useAuthStore } from '@/store/authStore'
import { downloadJsonFile } from '@/utils/devSeedManager'
import { runSmokeTest, scenarios } from './orchestrator.js'
import { isSmokeRunnerAvailable } from './availability.js'
import { cleanupSmokeArtifacts, countSmokeArtifacts } from './cleanup.js'
import { clearDebugUserSpoof, loadSmokeAuthInspection } from './authContext.js'
import {
  assertCompleteCheckAllowed,
  completeCheckRoleValues,
  runCompleteSmokeCheck,
  scenarioIdsForPreset
} from './completeCheck.js'
import { WORKSPACE_ROTATOR_ROLES, isWorkspaceRotatorEmail } from '@/utils/workspaceRoleRotator'
import { groupSelectionState, scenariosByGroup } from './scenarioGroups.js'
import { REFERRAL_PROBE_ROLES } from './referralAccessPlan.js'
import { createSmokeCloudReport } from './cloudReport.js'
import { pendingSmokeProfile, restoreSmokeProfile } from './fixtureSession.js'
import {
  IMPORT_UI_CHECKS,
  importUiResults,
  loadImportUiChecks,
  saveImportUiChecks
} from './importSmokePlan.js'
import { summarizeSmokeResults } from './stepStatus.js'

const available = isSmokeRunnerAvailable()
const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID
const authStore = useAuthStore()
const jwtRole = ref(null)
const authBlocked = ref(false)
const authBlockReason = ref('')
const leftover = ref(null)
const pendingProfile = ref(pendingSmokeProfile())
const importUiChecks = IMPORT_UI_CHECKS
const manualChecks = reactive(loadImportUiChecks(import.meta.url))
watch(manualChecks, (value) => saveImportUiChecks(import.meta.url, value), { deep: true })
const savingManual = ref(false)
const manualReport = ref(null)
const manualSaveMessage = ref('')

function resetManualReview() {
  for (const key of Object.keys(manualChecks)) delete manualChecks[key]
  manualReport.value = null
  manualSaveMessage.value = ''
}

async function recoverProfile() {
  isRunning.value = true
  runError.value = ''
  try {
    await restoreSmokeProfile()
  } catch (err) {
    runError.value = err.message
  } finally {
    pendingProfile.value = pendingSmokeProfile()
    await refreshAuthContext()
    isRunning.value = false
  }
}

async function saveManualReview() {
  savingManual.value = true
  manualSaveMessage.value = ''
  try {
    await refreshAuthContext()
    if (authBlocked.value) throw new Error(authBlockReason.value)
    const now = new Date().toISOString()
    const results = importUiResults(manualChecks)
    manualReport.value = {
      runId: `smoke-import-ui-${now.replace(/[:.]/g, '-')}`,
      startedAt: now,
      finishedAt: now,
      projectId,
      user: { role: jwtRole.value },
      results,
      summary: summarizeSmokeResults(results)
    }
    const log = createSmokeCloudReport((status) => {
      manualSaveMessage.value =
        status.status === 'saved'
          ? `UI review saved: ${status.path}`
          : status.error
            ? `Not saved: ${status.error}. Export the UI review or retry.`
            : 'Saving UI review…'
    })
    await log.finish(manualReport.value, 'completed')
  } catch (err) {
    manualSaveMessage.value = `Not saved: ${err.message}`
  } finally {
    savingManual.value = false
  }
}

function clearSpoofAndReload() {
  clearDebugUserSpoof()
  window.location.reload()
}

async function refreshAuthContext() {
  try {
    const loaded = await loadSmokeAuthInspection(authStore.currentUser)
    jwtRole.value = loaded.inspection.jwtRole
    authBlocked.value = loaded.inspection.blocked
    authBlockReason.value = loaded.inspection.reasons.join(' ')
  } catch (err) {
    authBlocked.value = true
    authBlockReason.value = err?.message || String(err)
  }
}

async function refreshLeftovers() {
  try {
    leftover.value = await countSmokeArtifacts()
  } catch (err) {
    leftover.value = {
      leftoverDocs: null,
      leftoverFiles: null,
      errors: [err.message],
      source: 'unavailable'
    }
  }
}

const leftoverLine = computed(() => {
  const L = leftover.value
  if (!L) return ''
  if (L.source === 'unavailable') {
    return `Leftover count unavailable until cleanupSmokeArtifacts is deployed (${L.errors?.[0] || 'error'}).`
  }
  const docs = L.leftoverDocs ?? 0
  const files = L.leftoverFiles ?? 0
  const src = L.source === 'admin' ? 'Admin SDK' : 'client query'
  return `Leftovers: ${docs} doc(s), ${files} file(s) (${src}).`
})

const leftoverWarn = computed(() => (leftover.value?.leftoverDocs || 0) > 0)

// Read-only scenarios are pre-selected; destructive ones are opt-in.
const includeWrites = ref(false)
const selected = ref(scenarioIdsForPreset(false))
const confirmDestructive = ref(false)
const keepArtifacts = ref(false)
const isRunning = ref(false)
const runMode = ref(null)
const sweepProgress = ref(null)
const runError = ref('')
const report = ref(null)
const cloudStatus = ref(null)
let cloudLog = null
let latestCloudReport = null

function startCloudLog() {
  cloudStatus.value = null
  latestCloudReport = null
  cloudLog = createSmokeCloudReport((status) => {
    cloudStatus.value = status
  })
}

function publishCloudReport(value) {
  latestCloudReport = value
  cloudLog?.publish(value)
}

async function finishCloudLog() {
  if (!latestCloudReport) return
  latestCloudReport.finishedAt = new Date().toISOString()
  if (runError.value) latestCloudReport.runError = runError.value
  await cloudLog.finish(latestCloudReport, runError.value ? 'aborted' : 'completed')
}

async function retryCloudSave() {
  await cloudLog?.retry()
}
// Separate reactive holder so step records streaming in re-render live.
const liveResults = reactive({ list: [] })

const completeRoleCount = completeCheckRoleValues().length
const rotatorRoles = WORKSPACE_ROTATOR_ROLES
const selectedRoles = ref(completeCheckRoleValues())

const allRolesBox = ref(null)
const allRolesSelected = computed(
  () => selectedRoles.value.length > 0 && selectedRoles.value.length === rotatorRoles.length
)
const someRolesSelected = computed(
  () => selectedRoles.value.length > 0 && selectedRoles.value.length < rotatorRoles.length
)

watch(someRolesSelected, (mixed) => {
  if (allRolesBox.value) allRolesBox.value.indeterminate = mixed
})

function toggleAllRoles() {
  selectedRoles.value = allRolesSelected.value ? [] : completeCheckRoleValues()
}

const canCompleteCheck = computed(() => {
  const email = authStore.currentUser?.email
  return (
    isWorkspaceRotatorEmail(email) &&
    assertCompleteCheckAllowed({ email, jwtRole: jwtRole.value }).ok
  )
})

const completeCheckBlockedReason = computed(() => {
  const email = authStore.currentUser?.email
  if (!email) return ''
  if (isWorkspaceRotatorEmail(email)) {
    const gate = assertCompleteCheckAllowed({ email, jwtRole: jwtRole.value })
    return gate.ok ? '' : gate.reason
  }
  return 'Complete check is only on the allowlisted demo login. Sign in as admin@casemanagevue.com, or run this suite once per test-role account.'
})

const scenarioGroups = computed(() => scenariosByGroup(scenarios))

function groupState(group) {
  return groupSelectionState(group, selected.value)
}

function toggleGroup(group) {
  const ids = group.scenarios.map((s) => s.id)
  const allOn = ids.every((id) => selected.value.includes(id))
  if (allOn) {
    selected.value = selected.value.filter((id) => !ids.includes(id))
  } else {
    selected.value = [...new Set([...selected.value, ...ids])]
  }
  includeWrites.value = scenarios.some((s) => s.destructive && selected.value.includes(s.id))
}

function applyPreset(writes) {
  includeWrites.value = writes
  selected.value = scenarioIdsForPreset(writes)
  if (!writes) {
    confirmDestructive.value = false
    keepArtifacts.value = false
  }
}

function applyReferralPreset() {
  selected.value = ['readAccess', 'referralAccess']
  includeWrites.value = true
  confirmDestructive.value = false
  keepArtifacts.value = false
  selectedRoles.value = REFERRAL_PROBE_ROLES.filter((role) => role !== 'admin')
}

function applyWorkflowPreset(id) {
  selected.value = ['authBootstrap', id]
  includeWrites.value = true
  confirmDestructive.value = false
  keepArtifacts.value = false
}

const destructiveSelected = computed(() =>
  scenarios.some((s) => s.destructive && selected.value.includes(s.id))
)

const runDisabled = computed(
  () =>
    isRunning.value ||
    selected.value.length === 0 ||
    authBlocked.value ||
    !!pendingProfile.value ||
    savingManual.value ||
    (destructiveSelected.value && !confirmDestructive.value)
)

const hasResults = computed(() => liveResults.list.length > 0)

const sweepProgressLine = computed(() => {
  const p = sweepProgress.value
  if (!p) return 'Complete check…'
  const label = p.currentRole || ''
  const n = p.roleIndex && p.roleCount ? `${p.roleIndex}/${p.roleCount}` : ''
  const phase = p.phase === 'cleanup' ? 'cleanup' : p.phase === 'restore' ? 'restore' : p.phase
  return `${n} ${label} ${phase}`.trim()
})

function stepsForScenario(scenarioId, role = null) {
  return liveResults.list.filter(
    (r) => r.scenario === scenarioId && (role == null || r.role === role)
  )
}

const reportedScenarios = computed(() => {
  return scenarios
    .filter((s) => selected.value.includes(s.id))
    .map((s) => {
      const steps = stepsForScenario(s.id)
      return {
        id: s.id,
        label: s.label,
        steps,
        failed: steps.some((r) => r.status === 'fail'),
        blocked: steps.some((r) => r.status === 'blocked')
      }
    })
    .filter((s) => s.steps.length > 0)
})

const reportedGroups = computed(() => {
  const byId = Object.fromEntries(reportedScenarios.value.map((s) => [s.id, s]))
  return scenarioGroups.value
    .map((g) => ({
      ...g,
      scenarios: g.scenarios.map((s) => byId[s.id]).filter(Boolean)
    }))
    .filter((g) => g.scenarios.length > 0)
})

const reportedRoleGroups = computed(() => {
  if (report.value?.mode !== 'complete' && runMode.value !== 'complete') return null
  const roleEntries = report.value?.roles
  if (roleEntries?.length) {
    return roleEntries.map((entry) => ({
      ...entry,
      scenarios: scenarios
        .filter((s) => selected.value.includes(s.id))
        .map((s) => {
          const steps = stepsForScenario(s.id, entry.role)
          return {
            id: s.id,
            label: s.label,
            steps,
            failed: steps.some((r) => r.status === 'fail'),
            blocked: steps.some((r) => r.status === 'blocked')
          }
        })
        .filter((s) => s.steps.length > 0)
    }))
  }
  const seen = []
  for (const r of liveResults.list) {
    if (r.role && !seen.includes(r.role)) seen.push(r.role)
  }
  if (!seen.length) return null
  return seen.map((role) => ({
    role,
    label: role,
    status: liveResults.list.some((r) => r.role === role && r.status === 'fail')
      ? 'fail'
      : liveResults.list.some((r) => r.role === role && r.status === 'blocked')
        ? 'blocked'
        : 'pass',
    rotateError: null,
    summary: { pass: 0, fail: 0, skip: 0, blocked: 0 },
    scenarios: scenarios
      .filter((s) => selected.value.includes(s.id))
      .map((s) => {
        const steps = stepsForScenario(s.id, role)
        return {
          id: s.id,
          label: s.label,
          steps,
          failed: steps.some((r) => r.status === 'fail'),
          blocked: steps.some((r) => r.status === 'blocked')
        }
      })
      .filter((s) => s.steps.length > 0)
  }))
})

function ingestProgress(payload) {
  const results = Array.isArray(payload) ? payload : payload?.results
  if (results) liveResults.list = [...results]
  if (payload && !Array.isArray(payload)) {
    sweepProgress.value = {
      currentRole: payload.currentRole,
      roleIndex: payload.roleIndex,
      roleCount: payload.roleCount,
      phase: payload.phase
    }
  }
}

async function run() {
  await refreshAuthContext()
  if (authBlocked.value) {
    runError.value = authBlockReason.value
    return
  }
  isRunning.value = true
  runMode.value = 'single'
  sweepProgress.value = null
  runError.value = ''
  liveResults.list = []
  report.value = null
  startCloudLog()
  try {
    report.value = await runSmokeTest(selected.value, {
      onProgress: (results) => ingestProgress(results),
      onReport: publishCloudReport
    })
    liveResults.list = [...report.value.results]
    if (destructiveSelected.value && !keepArtifacts.value) {
      cleanupSummary.value = await cleanupSmokeArtifacts()
      report.value.cleanup = cleanupSummary.value
    }
    await refreshAuthContext()
    await refreshLeftovers()
  } catch (err) {
    runError.value = err?.message || String(err)
  } finally {
    await finishCloudLog()
    pendingProfile.value = pendingSmokeProfile()
    isRunning.value = false
    runMode.value = null
  }
}

async function runComplete() {
  await refreshAuthContext()
  if (authBlocked.value) {
    runError.value = authBlockReason.value
    return
  }
  const gate = assertCompleteCheckAllowed({
    email: authStore.currentUser?.email,
    jwtRole: jwtRole.value
  })
  if (!gate.ok) {
    runError.value = gate.reason
    return
  }
  isRunning.value = true
  runMode.value = 'complete'
  sweepProgress.value = {
    phase: 'rotate',
    roleIndex: 0,
    roleCount: selectedRoles.value.length
  }
  runError.value = ''
  liveResults.list = []
  report.value = null
  startCloudLog()
  try {
    report.value = await runCompleteSmokeCheck(selected.value, {
      includeWrites: includeWrites.value,
      keepArtifacts: keepArtifacts.value,
      roles: selectedRoles.value,
      onProgress: ingestProgress,
      onReport: publishCloudReport
    })
    liveResults.list = [...report.value.results]
    if (report.value.restoreError) {
      runError.value = `Sweep finished but the starting role was not restored: ${report.value.restoreError}`
    }
    await refreshAuthContext()
    await refreshLeftovers()
  } catch (err) {
    runError.value = err?.message || String(err)
  } finally {
    await finishCloudLog()
    pendingProfile.value = pendingSmokeProfile()
    isRunning.value = false
    runMode.value = report.value?.mode === 'complete' ? 'complete' : null
    sweepProgress.value = null
  }
}

function exportReport() {
  if (!report.value) return
  downloadJsonFile(report.value, `${report.value.runId}-report.json`)
}

const isCleaning = ref(false)
const cleanupSummary = ref(null)

async function runCleanup() {
  isCleaning.value = true
  cleanupSummary.value = null
  try {
    cleanupSummary.value = await cleanupSmokeArtifacts()
    await refreshLeftovers()
  } catch (err) {
    cleanupSummary.value = { deleted: 0, storageDeleted: 0, errors: [err?.message || String(err)] }
  } finally {
    isCleaning.value = false
  }
}

onMounted(() => {
  refreshAuthContext()
  refreshLeftovers()
})
</script>

<style scoped>
.smoke-runner {
  font-size: 14px;
}
.tool-intro {
  color: var(--text-secondary, #555);
  margin-bottom: 12px;
}
.unavailable {
  padding: 10px 12px;
  background: var(--bg-secondary, #f5f5f5);
  border-radius: 6px;
  color: var(--text-secondary, #666);
}
.role-context {
  padding: 8px 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #1e3a8a;
}
.preset-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.import-ui-checks {
  padding: 10px;
  margin-bottom: 14px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
}
.import-ui-checks summary {
  cursor: pointer;
}
.preset-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary, #555);
}
.preset-btn {
  appearance: none;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 13px;
  cursor: pointer;
}
.preset-btn.active {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1e3a8a;
  font-weight: 600;
}
.preset-btn:disabled {
  opacity: 0.55;
  cursor: default;
}
.keep-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-bottom: 10px;
  font-size: 13px;
  color: var(--text-secondary, #555);
  cursor: pointer;
}
.keep-row input {
  margin-top: 3px;
}
.complete-note {
  padding: 8px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 10px;
  font-size: 13px;
}
.role-all {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-weight: 600;
  cursor: pointer;
}
.role-picks {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin-top: 6px;
}
.role-pick {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  cursor: pointer;
}
.muted-note {
  color: var(--text-secondary, #666);
}
.caveat {
  padding: 8px 10px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  margin-bottom: 10px;
  font-size: 12px;
  color: #92400e;
  line-height: 1.45;
}
.role-group {
  margin-bottom: 12px;
}
.role-group-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}
.role-group-header .pass {
  color: #15803d;
}
.role-group-header .fail {
  color: #b91c1c;
}
.role-group-header .blocked {
  color: #b45309;
}
.spoof-banner {
  padding: 8px 10px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 6px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #92400e;
}
.spoof-reset {
  display: block;
  margin-top: 8px;
  appearance: none;
  border: 1px solid #d97706;
  background: #fffbeb;
  color: #92400e;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
}
.leftover-line {
  margin-bottom: 10px;
  font-size: 13px;
  color: var(--text-secondary, #555);
}
.leftover-line.warn {
  color: #92400e;
}
.cleanup-summary {
  margin-bottom: 10px;
  font-size: 13px;
  color: var(--text-secondary, #555);
}
.scenario-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 10px;
}
.scenario-group {
  border: 1px solid var(--border-color, #e2e2e2);
  border-radius: 6px;
  padding: 8px 10px;
}
.group-header {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  cursor: pointer;
  margin-bottom: 6px;
}
.group-header input {
  margin-top: 3px;
}
.scenario-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  cursor: pointer;
  padding-left: 22px;
}
.scenario-row input {
  margin-top: 3px;
}
.scenario-label {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: baseline;
}
.scenario-desc {
  color: var(--text-secondary, #777);
  font-size: 13px;
}
.destructive-tag {
  background: #fee2e2;
  color: #b91c1c;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}
.confirm-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 10px;
  cursor: pointer;
}
.confirm-row input {
  margin-top: 3px;
}
.run-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.run-error {
  color: #b91c1c;
  margin-bottom: 10px;
}
.summary-line {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  margin-bottom: 10px;
}
.summary-line.all-pass {
  color: #15803d;
}
.summary-line.has-fail {
  color: #b91c1c;
}
.summary-line .muted {
  font-weight: 400;
  color: var(--text-secondary, #777);
  font-size: 13px;
}
.scenario-result {
  border: 1px solid var(--border-color, #e2e2e2);
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 8px;
}
.scenario-result-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}
.scenario-result-header .pass {
  color: #15803d;
}
.scenario-result-header .fail {
  color: #b91c1c;
}
.scenario-result-header .blocked {
  color: #b45309;
}
.step-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 2px 0 2px 4px;
  font-size: 13px;
  flex-wrap: wrap;
}
.step-row.pass .step-icon {
  color: #15803d;
}
.step-row.fail .step-icon {
  color: #b91c1c;
}
.step-row.blocked .step-icon {
  color: #b45309;
}
.step-row.skip .step-icon {
  color: #9ca3af;
}
.step-icon {
  display: inline-flex;
  align-self: center;
}
.step-detail {
  color: var(--text-secondary, #777);
}
.step-error {
  color: #b91c1c;
}
.step-ms {
  margin-left: auto;
  color: var(--text-secondary, #999);
  font-size: 12px;
}
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
